import { Check, X } from "lucide-react";

/**
 * Live password strength checker. Mirrors backend rules in
 * server.py `validate_password_strength`. Pure UI — non-blocking.
 */
const RULES = [
  { key: "min", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "An uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "A lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "A number (0-9)", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "A symbol (!@#$ …)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function PasswordStrength({ password = "" }) {
  const results = RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const passed = results.filter((r) => r.ok).length;
  const strength = passed / RULES.length;
  const barColor =
    strength < 0.4 ? "bg-red-500" : strength < 0.8 ? "bg-amber-500" : "bg-emerald-500";

  if (!password) return null;

  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-200`}
          style={{ width: `${strength * 100}%` }}
        />
      </div>
      <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {results.map((r) => (
          <li
            key={r.key}
            className={`flex items-center gap-1.5 ${
              r.ok ? "text-emerald-700" : "text-zinc-500"
            }`}
            data-testid={`pw-rule-${r.key}`}
            data-passed={r.ok ? "yes" : "no"}
          >
            {r.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function isPasswordStrong(password) {
  return RULES.every((r) => r.test(password));
}
